/**
 * Turn the read-only /kpi/source-audit payload into a plain-text provenance
 * report a person can read, copy, and carry back to the source application.
 *
 * Deliberately text, not a component: the point of the report is that it can be
 * pasted into an email or a ticket unchanged. Nothing here recomputes a figure —
 * every number printed is one the server read back out of the stored snapshot.
 */

const n = (v) => (v === null || v === undefined ? '—' : String(v));

function verdict(u) {
  if (!u.path_found_in_snapshot) return 'SOURCE FIELD NOT TRACEABLE';
  if (u.copied_faithfully === false) return 'MISMATCH (catalogue differs from source file)';
  if (u.derived_value === null || u.derived_value === undefined) return 'DIRECT SOURCE';
  if (u.derivation_matches) return 'DIRECT SOURCE · re-derivation agrees';
  return 'AMBIGUOUS (source figure does not reconcile with its own components)';
}

export function auditToText(d) {
  const L = [];
  const push = (s = '') => L.push(s);

  push('='.repeat(66));
  push(`${(d.source_declared || d.source || '').toUpperCase().replace(/_/g, ' ')} — DATA SOURCE AUDIT`);
  push(`Period: ${d.month} ${d.fy}`);
  push('='.repeat(66));
  push();
  push('WHAT THIS REPORT IS');
  push('  For every number this source supplied, it shows the value stored in');
  push('  HR, the value found at the same field in the file the source sent,');
  push('  and — where the number can be rebuilt from other fields in that same');
  push('  file — the rebuilt value. Read-only; nothing was changed.');
  push();
  push('IMPORT BEHAVIOUR');
  push('  HR performs NO arithmetic on an imported figure. Each value is stored');
  push('  verbatim from the source file. Any formula shown below is the SOURCE');
  push("  application's own arithmetic, re-checked here against the components");
  push('  it supplied alongside the figure.');
  push();
  if (d.period) {
    push(`SOURCE PERIOD WINDOW: ${n(d.period.from)} to ${n(d.period.to)}`
      + (d.period.counted_to ? ` (counted to ${d.period.counted_to})` : ''));
  }
  push(`SOURCE GENERATED AT:  ${n(d.generated_at_source)}`);
  push(`MONTH TO DATE:        ${d.month_to_date ? 'YES — partial month' : 'NO — complete month'}`);
  if (d.source_note) push(`SOURCE NOTE:          ${d.source_note}`);
  push();
  push('HOW THIS DATA REACHED HR');
  (d.snapshot_provenance || []).forEach((s) => {
    push(`  ${s.unit === null ? '(whole file)' : s.unit}`);
    push(`     brought in by: ${n(s.uploaded_by)}`);
    push(`     brought in at: ${n(s.uploaded_at)}`);
    if (s.file_name) push(`     file name:     ${s.file_name}`);
  });
  push();
  if (d.unavailable_fields && Object.keys(d.unavailable_fields).length) {
    push('FIELDS THE SOURCE STATES IT CANNOT SUPPLY');
    Object.entries(d.unavailable_fields).forEach(([k, v]) => {
      push(`  ${k}`);
      push(`     ${v}`);
    });
    push();
  }
  push(`UNITS AUDITED: ${(d.units_audited || []).join(', ') || '—'}`);
  push(`NUMBERS AUDITED: ${d.metric_count}`);
  push();

  (d.metrics || []).forEach((m, i) => {
    push('-'.repeat(66));
    push(`${i + 1}. ${m.label}`);
    push('-'.repeat(66));
    push(`   Source application:   ${(d.source_declared || d.source || '').replace(/_/g, ' ')}`);
    push(`   Source dataset:       metric_snapshots · source="${d.source}" · fy=${d.fy} · month=${d.month}`);
    push(`   Source field / path:  ${m.path}`);
    push(`   Unit of measure:      ${n(m.uom)}`);
    push(`   Reported per:         ${n(m.scope)}`);
    push(`   First seen in HR:     ${n(m.first_seen)}`);
    push(`   Last refreshed:       ${n(m.last_seen)} (${n(m.last_fy)} ${n(m.last_month)})`);
    push(`   Transport:            ${m.transport}`);
    push();
    (m.units || []).forEach((u) => {
      push(`   ── ${u.unit}  [source calls this "${n(u.source_unit_key)}" / "${n(u.source_unit_name)}"]`);
      push(`      Value shown in HR:        ${n(u.catalogue_value)}`);
      push(`      Value in the source file: ${n(u.snapshot_value)}`);
      push(`      Faithful copy:            ${u.copied_faithfully === null ? 'n/a' : (u.copied_faithfully ? 'YES' : 'NO')}`);
      push(`      Filter applied:           unit = ${u.unit} · month = ${d.month} ${d.fy}`
        + (d.period ? ` (${d.period.from} to ${d.period.to})` : ''));
      if (u.formula) {
        push(`      Source formula:           ${u.formula}`);
        push(`      Rebuilt from components:  ${n(u.derived_value)}`);
        push(`      Rebuild agrees:           ${u.derivation_matches ? 'YES' : 'NO'}`);
        push('      Calculated or copied:     CALCULATED BY SOURCE, copied into HR');
      } else {
        push('      Source formula:           none — supplied as a finished figure');
        push('      Calculated or copied:     DIRECTLY COPIED');
      }
      if (u.record_set) {
        push(`      Backing records:          ${u.record_set} · ${n(u.record_count)} row(s)`);
        (u.records || []).forEach((r, ri) => {
          const bits = Object.entries(r)
            .filter(([, v]) => v !== null && v !== undefined)
            .map(([k, v]) => `${k}=${v}`).join(', ');
          push(`         Record ${ri + 1} → ${bits}`);
        });
      }
      push(`      Status:                   ${verdict(u)}`);
      push();
    });
  });

  // ---- summary table -------------------------------------------------------
  push('='.repeat(66));
  push('SUMMARY TABLE');
  push('='.repeat(66));
  push('| #  | Number | Unit | HR value | Source field | Calculation | Status |');
  push('|----|--------|------|----------|--------------|-------------|--------|');
  let direct = 0; let calculated = 0; let ambiguous = 0; let missing = 0;
  (d.metrics || []).forEach((m, i) => {
    (m.units || []).forEach((u) => {
      const v = verdict(u);
      if (v.startsWith('SOURCE FIELD NOT')) missing += 1;
      else if (v.startsWith('AMBIGUOUS') || v.startsWith('MISMATCH')) ambiguous += 1;
      else if (u.formula) calculated += 1;
      else direct += 1;
      push(`| ${i + 1} | ${m.label} | ${u.unit} | ${n(u.catalogue_value)} | ${m.path} | `
        + `${u.formula || 'none (direct)'} | ${v} |`);
    });
  });
  push();
  const totalChecks = direct + calculated + ambiguous + missing;
  push(`TOTAL NUMBERS:            ${d.metric_count}`);
  push(`TOTAL UNIT-LEVEL CHECKS:  ${totalChecks}`);
  push(`DIRECT SOURCE:            ${direct}`);
  push(`CALCULATED BY SOURCE:     ${calculated}`);
  push(`AMBIGUOUS:                ${ambiguous}`);
  push(`MISSING SOURCE:           ${missing}`);
  push();
  const bad = [];
  (d.metrics || []).forEach((m) => (m.units || []).forEach((u) => {
    if (!u.path_found_in_snapshot || u.copied_faithfully === false
      || (u.derived_value !== null && u.derived_value !== undefined && !u.derivation_matches)) {
      bad.push(`${m.label} · ${u.unit}`);
    }
  }));
  push(`RESULT: ${bad.length === 0 ? 'PASS — every number traced to the source field it came from'
    : `FAIL — ${bad.length} number(s) did not reconcile`}`);
  if (bad.length) bad.forEach((b) => push(`   • ${b}`));
  push();
  push('VERIFY THESE FIRST (they involve arithmetic, so an error here propagates):');
  (d.metrics || []).filter((m) => (m.units || []).some((u) => u.formula))
    .forEach((m) => push(`   • ${m.label} — ${m.units.find((u) => u.formula).formula}`));
  push();
  push('='.repeat(66));
  return L.join('\n');
}
